import { useEffect, useRef, useState } from 'react'
import { COLORS } from '../lib/theme'
import { Icon } from './Icon'

/** Approximate rendered height of the time picker popover in px. */
const POPOVER_HEIGHT = 230
const POPOVER_WIDTH  = 216
const GAP = 6

/** Compute `position:fixed` coordinates for the popover from the trigger rect. */
function resolvePopoverPos(rect: DOMRect, height: number, width: number): React.CSSProperties {
  const spaceBelow = window.innerHeight - rect.bottom
  const left = Math.min(rect.left, window.innerWidth - width - 8)

  if (spaceBelow >= height + GAP) {
    return { position: 'fixed', top: rect.bottom + GAP, left }
  }
  // Not enough room below — open above the trigger
  return { position: 'fixed', bottom: window.innerHeight - rect.top + GAP, left }
}

export interface WaTimePickerProps {
  value: string        // HH:MM or ''
  onChange: (v: string) => void
}

export function WaTimePicker({ value, onChange }: WaTimePickerProps) {
  const [open, setOpen] = useState(false)
  const [popoverPos, setPopoverPos] = useState<React.CSSProperties>({})
  const [hh, setHh] = useState('')
  const [mm, setMm] = useState('')
  const [focus, setFocus] = useState<'hh' | 'mm'>('hh')

  const triggerRef = useRef<HTMLButtonElement>(null)
  const hhRef = useRef<HTMLInputElement>(null)
  const mmRef = useRef<HTMLInputElement>(null)

  const openPicker = () => {
    if (triggerRef.current) {
      setPopoverPos(
        resolvePopoverPos(triggerRef.current.getBoundingClientRect(), POPOVER_HEIGHT, POPOVER_WIDTH),
      )
    }
    const [h = '', m = ''] = (value || '').split(':')
    setHh(h)
    setMm(m)
    setFocus('hh')
    setOpen(true)
  }

  // Auto-focus HH when picker opens
  useEffect(() => {
    if (open) hhRef.current?.focus()
  }, [open])

  // Auto-advance focus to MM when HH is 2 digits
  useEffect(() => {
    if (focus === 'mm') mmRef.current?.focus()
  }, [focus])

  const confirm = () => {
    const h = Math.max(0, Math.min(23, parseInt(hh) || 0))
    const m = Math.max(0, Math.min(59, parseInt(mm) || 0))
    onChange(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
    setOpen(false)
  }

  const handleHhInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2)
    setHh(raw)
    if (raw.length === 2) setFocus('mm')
  }

  const handleMmInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2)
    setMm(raw)
  }

  // Both fields open pre-filled and a full field ignores typing, so each one
  // selects its text on focus: the user types straight over it, including
  // after two hour digits move focus to the minutes.
  const handleHhFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocus('hh')
    e.currentTarget.select()
  }

  const handleMmFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocus('mm')
    e.currentTarget.select()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') confirm()
    if (e.key === 'Escape') setOpen(false)
  }

  const fieldStyle = (active: boolean): React.CSSProperties => ({
    width: '100%',
    height: 72,
    borderRadius: 8,
    border: `2px solid ${active ? COLORS.primary : COLORS.outlineVariant}`,
    background: COLORS.surfaceContainerLowest,
    fontSize: 40,
    fontWeight: 500,
    textAlign: 'center',
    color: COLORS.onSurface,
    outline: 'none',
    boxShadow: active ? `0 0 0 3px var(--nl-primary-opacity-12)` : 'none',
    transition: 'border-color 150ms, box-shadow 150ms',
    caretColor: COLORS.primary,
  })

  return (
    <div style={{ position: 'relative' }}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        className="wh-input"
        onClick={openPicker}
        aria-label="Pick time"
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          cursor: 'pointer', width: '100%', textAlign: 'left',
          color: value ? COLORS.onSurface : COLORS.outline,
        }}
      >
        <span style={{ flex: 1, fontSize: 14 }}>
          {value || '--:--'}
        </span>
        <Icon name="clock" size={16} color={COLORS.outline} />
      </button>

      {open && (
        <>
          {/* Click-outside overlay */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 1200 }}
            onClick={() => setOpen(false)}
          />

          {/* Keyboard time picker */}
          <div
            role="dialog"
            aria-label="Time picker"
            style={{
              ...popoverPos,
              zIndex: 1201,
              background: COLORS.surfaceContainerLowest,
              borderRadius: 12,
              border: `1px solid ${COLORS.surfaceContainerHighest}`,
              boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
              padding: '18px 20px 14px',
              minWidth: POPOVER_WIDTH,
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleKeyDown}
          >
            {/* Title */}
            <div style={{
              fontSize: 11, fontWeight: 600, color: COLORS.outline,
              textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14,
            }}>
              Enter time
            </div>

            {/* HH : MM */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <input
                  ref={hhRef}
                  type="text"
                  inputMode="numeric"
                  value={hh}
                  onChange={handleHhInput}
                  onFocus={handleHhFocus}
                  placeholder="HH"
                  maxLength={2}
                  aria-label="Hour"
                  style={fieldStyle(focus === 'hh')}
                />
                <div style={{ fontSize: 11, textAlign: 'center', color: COLORS.outline, marginTop: 6 }}>
                  Hour
                </div>
              </div>

              <div style={{
                fontSize: 36, fontWeight: 300, color: COLORS.onSurface,
                lineHeight: '72px', userSelect: 'none',
              }}>
                :
              </div>

              <div style={{ flex: 1 }}>
                <input
                  ref={mmRef}
                  type="text"
                  inputMode="numeric"
                  value={mm}
                  onChange={handleMmInput}
                  onFocus={handleMmFocus}
                  placeholder="MM"
                  maxLength={2}
                  aria-label="Minute"
                  style={fieldStyle(focus === 'mm')}
                />
                <div style={{ fontSize: 11, textAlign: 'center', color: COLORS.outline, marginTop: 6 }}>
                  Minute
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                className="wh-btn wh-btn-text"
                onClick={() => setOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="wh-btn wh-btn-primary"
                onClick={confirm}
              >
                OK
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
